import React from 'react';
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core';
import Seo from 'sharedComponents/Seo';
import { useTranslation } from 'react-i18next';

import useStyles from './useStyles';

const PrivacyPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.container}>
      <Seo title={t('meta.PrivacyPage.title')} />
      <Box className={classes.containerInside}>
        <Typography variant="h1" paragraph>
          Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Types of Information We Collect:</b>
        </Typography>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>Context</TableCell>
              <TableCell>Types of Data</TableCell>
              <TableCell>Primary Purpose for Collection and Use of Data</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Account Registration</TableCell>
              <TableCell>
                We collect your name and contact information when you create an account. We also collect information
                relating to the actions that you perform while logged into your account.
              </TableCell>
              <TableCell>
                We have a legitimate interest in providing account related functionalities to our users. Accounts can be
                used to save your information and bookmark where you were the last time you were logged in.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Cookies and First Party Tracking</TableCell>
              <TableCell>
                We use cookies and clear GIFs. &quot;Cookies&quot; are small pieces of information that a website sends
                to a computer&apos;s hard drive while a web site is viewed. We may use both session cookies (which
                expire once a web browser is closed) and persistent cookies (which stay on a device until deleted).
                Among other things, cookies allow us to provide a more personal and interactive experience and to
                improve our marketing efforts. Persistent cookies may be removed by following instructions provided by
                your browser. If you choose to disable cookies some areas or features of our websites may not work
                properly.
              </TableCell>
              <TableCell>We have a legitimate interest in making our website operate efficiently</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Cookies and Third Party Tracking</TableCell>
              <TableCell>
                We participate in behavior-based advertising. This means that a third party uses technology (e.g., a
                cookie) to collect information about your use of our website so that they can provide advertising about
                products and services tailored to your interests. That advertising may appear either on our websites, or
                on other websites. You can opt-out of receiving advertising based upon your browsing behavior from some
                network advertising companies by going to the Network Advertising Initiative and the Digital Advertising
                Alliance websites, although to completely prevent advertising based upon your browsing behavior you
                should also disable the cookies on your browser.
              </TableCell>
              <TableCell>We have a legitimate interest in engaging in behavior-based advertising.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Demographic Information</TableCell>
              <TableCell>We collect personal information, such as your location.</TableCell>
              <TableCell>
                We have a legitimate interest in understanding our users and providing tailored services.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Feedback/Support</TableCell>
              <TableCell>
                If you provide us feedback or contact us for support we will collect your name and e-mail address, as
                well as any other content that you send to us, in order to reply.
              </TableCell>
              <TableCell>
                We have a legitimate interest in receiving, and acting upon, your feedback or issues.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Financial Documents</TableCell>
              <TableCell>
                If you upload and store your financial documents in preparation for obtaining a mortgage, we provide
                services to help you organize these documents, and, if you choose, transmit them to a loan officer.
              </TableCell>
              <TableCell>We have a legitimate interest in providing you services you request.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Mailing List</TableCell>
              <TableCell>
                When you sign up for one of our mailing lists, we collect your email address or postal address.
              </TableCell>
              <TableCell>
                We have a legitimate interest in sharing information about our products or services.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Mortgage Bids and Budget</TableCell>
              <TableCell>
                When you decide to receive mortgage bids or complete a budget, we collect information including, but not
                limited to, your down payment target, your home price target, your credit score estimate, purchase
                purpose, expenses, and income.
              </TableCell>
              <TableCell>We have a legitimate interest in providing you services you request.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Partner Promotion</TableCell>
              <TableCell>
                We collect information that you provide as part of a co-branded promotion with another company.
              </TableCell>
              <TableCell>We have a legitimate interest in fulfilling our promotions.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Surveys</TableCell>
              <TableCell>
                When you participate in a survey we collect information that you provide through the survey. If the
                survey is provided by a third party service provider, the third party&apos;s privacy policy applies to
                the collection, use, and disclosure of your information.
              </TableCell>
              <TableCell>
                We have a legitimate interest in understanding your opinions, and collecting information relevant to our
                organization.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Website interactions</TableCell>
              <TableCell>
                We use technology to monitor how you interact with our website. This may include which links you click
                on, or information that you type into our online forms.
              </TableCell>
              <TableCell>
                We have a legitimate interest in understanding how you interact with our website to better improve it,
                and to understand your preferences and interests in order to select offerings that you might find most
                useful.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Typography variant="body1" paragraph className={classes.termSection}>
          In addition to the information that we collect from you directly, we may also receive information about you
          from other sources, including third parties, business partners, our affiliates, or publicly available sources
          . For example, if you submit a job application, or become an employee, we may conduct a background check.
        </Typography>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Use And Processing of Information: </b>In addition to the purposes and uses described above, we use
          information in the following ways:
        </Typography>
        <ul className={classes.listText}>
          <li>To identify you when you visit our website.</li>
          <li>To provide products and services.</li>
          <li>To improve our services and product offerings.</li>
          <li>To improve our website.</li>
          <li>To conduct analytics.</li>
          <li>To respond to inquiries related to support, or other requests.</li>
          <li>
            To send marketing and promotional materials, including information relating to our products, and services.
          </li>
          <li>For internal administrative purposes, as well as to manage our relationships.</li>
        </ul>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Sharing Of Information:</b> In addition to the specific situations discussed elsewhere in this policy, we
          disclose information in the following situations:
        </Typography>
        <ul className={classes.listText}>
          <li>
            1. Affiliates and Acquisitions. We may share information with our corporate affiliates (e.g., parent
            company, sister companies, subsidiaries, joint ventures, or other companies under common control). If
            another company acquires, or plans to acquire, our company, business, or our assets, we will also share
            information with that company, including at the negotiation stage.
          </li>
          <li>
            2. Other Disclosures with Your Consent. We may ask if you wolid like us to share your information with other
            unaffiliated third parties who are not described elsewhere in this policy.
          </li>
          <li>
            3. Other Disclosures without Your Consent. We may disclose information in response to subpoenas, warrants,
            or court orders, or in connection with any legal process, or to comply with relevant laws. We may also share
            your information in order to establish or exercise our rights, to defend against a legal claim, to
            investigate, prevent, or take action regarding possible illegal activities, suspected fraud, safety of
            person or property, or a violation of our policies, or to comply with your request for the shipment of
            products to or the provision of services by a third party intermediary.
          </li>
          <li>
            4. Public. Some of our websites may provide the opportunity to post comments, or reviews, in a public forum.
            If you decide to submit information on these pages, that information may be publicly available.
          </li>
          <li>
            5. Business Partners. In submitting an inquiry for a loan product or other service provided through this
            website, you agree that Bundle may share your Information with loan officers, lenders and other third
            parties which provide services to our consumers (including, for example, real estate agents or debt relief
            companies) to deliver the products and services your request.
          </li>
          <li>
            6. Service Providers. We may share your information with service providers including, but not limited to,
            credit bureaus, marketing partners, or entities providing business analysis and/or website or product
            support.. Among other things service providers may help us to administer our website, and provide technical
            support.
          </li>
        </ul>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Your Choices: </b>You can make the following choices regarding your personal information:
        </Typography>
        <ul className={classes.listText}>
          <li>
            1. Promotional Emails. You may choose to provide us with your email address for the purpose of allowing us
            to send free newsletters, surveys, offers, and other promotional materials to you, as well as targeted
            offers from third parties. You can stop receiving promotional emails by following the unsubscribe
            instructions in e-mails that you receive. If you decide not to receive promotional emails, we may still send
            you service related communications.
          </li>
          <li>
            2. Promotional Mailings. If at any time you do not want to receive offers and/or circliars from us you can
            remove yourself from our mailing lists by emailing us (our contact information is below) with &quot;NO SNAIL
            MAIL&quot; in the subject line along with your name, address and zip code. Please note that our mailings are
            prepared in advance of their being sent. Although we will remove your name from our mailing list after
            receiving your request, you may still receive mailings from us that had been initiated prior to your name
            being removed.
          </li>
          <li>
            3. Access to Your Personal Information. If required by law, upon request, we will grant you reasonable
            access to the personal information that we have about you. You may request access to your personal
            information by contacting us at the address described below.
          </li>
          <li>
            4. Changes to Your Personal Information. We rely on you to update and correct your personal information. Our
            website allows you to modify or delete your account profile. Note that we may keep historical information in
            our backup files as permitted by law. If our website does not permit you to update or correct certain
            information contact us at the address described below.
          </li>
          <li>
            5. Deletion Of Your Personal Information. Typically we retain your personal information for the period
            necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or
            permitted by law. You may, however, request information about how long we keep a specific type of
            information, or request that we delete your personal information by contacting us at the address described
            below. In addition, we may keep your personal information as needed to comply with our legal obligations,
            resolve disputes, and/or enforce any of our agreements.
          </li>
          <li>
            6. Revocation Of Consent. If you revoke your consent for the processing of personal information then we may
            no longer be able to provide you services. In some cases, we may limit or deny your request to revoke
            consent if the law permits or requires us to do so, or if we are unable to adequately verify your identity.
            You may revoke consent to processing (where such processing is based upon consent) by contacting us at the
            address described below.
          </li>
          <li>
            7. Online Tracking. We do not currently recognize automated browser signals regarding tracking mechanisms,
            which may include &quot;Do Not Track&quot; instructions.
          </li>
          <li>
            8. California Residents. California residents may be entitled to ask us for a notice describing what
            categories of personal information (if any) we share with third parties or affiliates for direct marketing.
            If you are a California resident and wolid like a copy of such notice, please submit a written request to us
            using the information in the &quot;Contact Information&quot; section below.
          </li>
        </ul>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>How We Protect Personal Information:</b> No method of transmission over the Internet, or method of
          electronic storage, is fully secure. While we use reasonable efforts to protect your personal information from
          unauthorized access, use, or disclosure, we cannot guarantee the security of your personal information. In the
          event that we are required by law to inform you of any unauthorized access to your personal information we may
          notify you electronically, in writing, or by telephone, if permitted to do so by law.
        </Typography>
        <Typography variant="body1" paragraph className={classes.termSection}>
          Some of our websites permit you to create an account. When you do you will be prompted to create a password.
          You are responsible for maintaining the confidentiality of your password, and you are responsible for any
          access to or use of your account by someone else that has obtained your password, whether or not such access
          or use has been authorized by you. You should notify us of any unauthorized use of your password or account.
        </Typography>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Miscellaneous:</b> The following additional information relates to our privacy practices:
        </Typography>
        <ul className={classes.listText}>
          <li>
            Transmission Of Information To Other Countries. Bundle is a U.S. based company. By submitting your personal
            information to us you agree to the transfer, storage and processing of your information in a country other
            than your country of residence including, but not necessarily limited to, the United States.
          </li>
          <li>
            Third Party Applications/Websites. We have no control over the privacy practices of websites or applications
            that we do not own.
          </li>
          <li>
            Changes To This Privacy Policy. We may change our privacy policy and practices over time. To the extent that
            our policy changes in a material way, the policy that was in place at the time that you submitted personal
            information to us will generally govern that information unless we receive your consent to the new privacy
            policy.
          </li>
        </ul>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Contact Information:</b> If you have any questions, comments, or complaints concerning our privacy
          practices please contact us at: <br />
          Mailing Adress: <br /> Bundle Marketplace Inc. <br /> 615 South College Street 9th Floor <br /> Charlotte, NC
          28202 <br /> <br /> Phone Number:
          <br /> (980) 260-1500 <br /> <br /> Email: <br /> <a href="mailto:info@bundleloan.com">info@bundleloan.com</a>
          <br /> <br />
          We will attempt to respond to your requests and to provide you with additional privacy-related information.
        </Typography>
        <Typography variant="body1" paragraph className={classes.termSection}>
          <b>Gramm-Leach-Bliley:</b>
        </Typography>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>FACTS</TableCell>
              <TableCell>
                WHAT DOES BUNDLE MARKETPLACE INC. (&quot;BUNDLE&quot;) DO WITH YOUR PERSONAL INFORMATION
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Why?</TableCell>
              <TableCell>
                Financial companies choose how they share your personal information. Federal law gives consumers the
                right to limit some but not all sharing. Federal law also requires us to tell you how we collect, share,
                and protect your personal information. Please read this notice carefully to understand what we do.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>What?</TableCell>
              <TableCell>
                The types of personal information we collect and share depend on the product or service you have with
                us. This information can include estimated credit scores, income, expenses, and account balances.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>How?</TableCell>
              <TableCell>
                All financial companies need to share customers&apos; personal information to run their everyday
                business. In the section below, we list the reasons financial companies can share their customers&apos;
                personal information; the reasons Bundle choose to share; and whether you can limit this sharing.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>Reasons we can share your personal information</TableCell>
              <TableCell>Does Bundle share?</TableCell>
              <TableCell>Can you limit sharing?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <b>For our everyday business purposes</b> <br /> such as to process your transactions, maintain your
                account(s), respond to court orders and legal investigations, or report to credit bureaus
              </TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>No</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For our marketing purposes</b> <br /> To offer our products and services to you
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For joint marketing with other financial companies</b> <br />
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For our affiliates&apos; everyday business purposes</b> <br /> information about your transactions
                and experiences
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For our affiliates&apos; everyday business purposes</b> <br /> information to help determine your
                creditworthiness
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For our affiliates to market to you</b>
                <br />
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>For our non-affiliates to market to you</b> <br />
              </TableCell>
              <TableCell>No</TableCell>
              <TableCell>We don&apos;t share</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>Questions?</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>
                If you have any questions, comments, or complaints concerning our privacy practices please contact us at
                the appropriate address listed on our Contact Page. We will attempt to respond to your requests and to
                provide you with additional privacy-related information.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>What we do</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Who is providing this notice?</TableCell>
              <TableCell>Bundle Marketplace Inc.</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>How does Bundle protect my personal information?</TableCell>
              <TableCell>
                To protect your personal information from unauthorized access and use, we use security measures that
                comply with federal law. These measures include computer safeguards and secured files and buildings.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>How does Bundle collect my personal information?</TableCell>
              <TableCell>
                We collect your personal information, for example, when you register an account, complete a budget, or
                request mortgage bids.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Why can&apos;t I limit all sharing?</TableCell>
              <TableCell>
                Federal law gives you the right to limit only:
                <br /> <br /> 1. Sharing for affiliates&apos; everyday business purposes - information about your
                creditworthiness <br /> 2. Affiliates from using your information to market to you <br /> 3. Sharing for
                non-affiliates to market to you <br /> <br /> State laws and individual companies may give you
                additional rights to limit sharing.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <Table>
          <TableHead className={classes.headerRow}>
            <TableRow>
              <TableCell>Definitions</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Affiliates</TableCell>
              <TableCell>
                Companies related by common ownership or control. They can be financial and non-financial companies.
                <br /> <br /> Bundle does not share with affiliates.
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Non-affiliates</TableCell>
              <TableCell>
                Companies not related by common ownership or control. They can be financial and non-financial companies.
                <br /> <br /> Bundle does not share with non-affiliates so they can market to you
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Joint Marketing</TableCell>
              <TableCell>
                A formal agreement between non-affiliated financial companies that together market financial products or
                services to you. <br /> Bundle doesn&apos;t jointly market.
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default PrivacyPage;



// WEBPACK FOOTER //
// ./src/components/Privacy/PrivacyPage.js